import ExcelJS from 'exceljs';

async function downloadWorkbook(
  workbook: ExcelJS.Workbook,
  filename: string,
): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Export JSON rows to an `.xlsx` download in the browser. */
export async function downloadExcelFromJson(
  rows: Record<string, unknown>[],
  sheetName: string,
  filename: string,
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName.substring(0, 31));
  if (rows.length === 0) {
    await downloadWorkbook(workbook, filename);
    return;
  }
  const headers = Object.keys(rows[0]);
  sheet.addRow(headers);
  for (const row of rows) {
    sheet.addRow(headers.map((header) => row[header] ?? ''));
  }
  await downloadWorkbook(workbook, filename);
}

function stringFromFormulaResult(result: ExcelJS.CellValue): string {
  if (typeof result === 'object') {
    return result instanceof Date ? result.toISOString() : JSON.stringify(result);
  }
  return String(result).trim();
}

function stringFromRichCell(value: object): string {
  if ('text' in value && value.text != null) {
    const text = value.text;
    if (typeof text === 'string' || typeof text === 'number' || typeof text === 'boolean') {
      return String(text).trim();
    }
    return JSON.stringify(text);
  }
  if ('result' in value && value.result != null) {
    return stringFromFormulaResult(value.result as ExcelJS.CellValue);
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return JSON.stringify(value);
}

function cellToString(value: ExcelJS.CellValue): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'object') {
    return stringFromRichCell(value);
  }
  return String(value).trim();
}

/** Read the first worksheet from an Excel file into plain row objects. */
export async function readExcelRowsFromArrayBuffer(
  buffer: ArrayBuffer,
): Promise<Record<string, unknown>[]> {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const sheet = workbook.worksheets[0];
  if (!sheet) {
    return [];
  }

  const headers: string[] = [];
  sheet.getRow(1).eachCell((cell, colNumber) => {
    headers[colNumber - 1] = cellToString(cell.value);
  });

  const rows: Record<string, unknown>[] = [];
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) {
      return;
    }
    const record: Record<string, unknown> = {};
    let hasValue = false;
    row.eachCell((cell, colNumber) => {
      const key = headers[colNumber - 1];
      if (!key) {
        return;
      }
      const text = cellToString(cell.value);
      record[key] = text;
      if (text) {
        hasValue = true;
      }
    });
    if (hasValue) {
      rows.push(record);
    }
  });
  return rows;
}
